
import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, DatePicker, Button, Table, message, Spin, Modal } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { useNavigate, useLocation } from 'react-router-dom';
import { LeftCircleOutlined } from '@ant-design/icons';
import { app } from '../../config';
import editBtnImg from '../../assets/images/edit.png';
import getHeaders from '../../utils/authHeaders';
// import { app } from '../config';

const FacialScanViewAll = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchForm] = Form.useForm();
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  const [tableData, setTableData] = useState([]);
  const [rewardCount, setRewardCount] = useState(0);

  const [showTable, setShowTable] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // =========================
  // SEARCH API
  // =========================
  const handleSearch = async (values) => {
    if (!values?.email) return;
    try {
      setLoading(true);
      // const response = await axios.get(`http://localhost:3200/api/v1/app/anura/check?email=${values.email}`);

      const response = await axios.get(`${app.URL}/api/v1/app/anura/check?email=${values.email}`);

      if (response?.data?.success) {
        setTableData(response?.data?.data?.data || []);
        setRewardCount(response?.data?.data?.rewardCount || 0);
        setShowTable(true);

        message.success('Data fetched successfully');
      } else {
        setTableData([]);
        setRewardCount(0);
        setShowTable(false);

        message.error(response?.data?.msg || 'No data found');
      }
    } catch (error) {
      console.log(error);

      setTableData([]);
      setRewardCount(0);
      setShowTable(false);

      message.error(error?.response?.data?.msg || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // AUTO FETCH ON MOUNT / NAVIGATION
  // =========================
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const emailParam = searchParams.get('email');
    const stateEmail = location.state?.email;
    const storedEmail = localStorage.getItem('facialScanEmail');

    const email = stateEmail || emailParam || storedEmail;

    if (email) {
      searchForm.setFieldsValue({ email });
      handleSearch({ email });
      if (storedEmail) {
        localStorage.removeItem('facialScanEmail');
      }
    }
  }, [location]);

  useEffect(() => {
    const isFacialScanGetRoute = window.location.pathname.includes('/facial-scan/get');
    if (!isFacialScanGetRoute) return undefined;

    const handleBackNavigation = () => {
      navigate('/facial-scan/view', { replace: true });
    };

    window.history.pushState(null, '', '/facial-scan/get');
    window.addEventListener('popstate', handleBackNavigation);

    return () => {
      window.removeEventListener('popstate', handleBackNavigation);
    };
  }, [navigate, location.pathname]);

  // =========================
  // ADD FACIAL SCAN API
  // =========================
  const handleAddFacialScan = async (values) => {
    try {
      setAddLoading(true);

      const payload = {
        email: values.email,
        scanCounts: values.scanCounts,
        expiryDate: values.date.format('YYYY-MM-DD'),
      };

      await axios.get(`${app.URL}/api/v1/static/addfacialreward?isWeb=true&email=${payload.email}&facialScans=${payload.scanCounts}&expiryDays=${payload.expiryDate}`);

      message.success('Facial reward added successfully');

      setOpenModal(false);
      addForm.resetFields();

      // Refresh listing automatically
      handleSearch({
        email: payload.email,
      });
    } catch (error) {
      console.log(error);

      message.error(error?.response?.data?.result?.msg || 'Something went wrong');
    } finally {
      setAddLoading(false);
    }
  };

  // =========================
  // EDIT MODAL HANDLERS
  // =========================
  const handleOpenEditModal = (record) => {
    setSelectedRecord(record);
    const currentEmail = searchForm.getFieldValue('email') || record?.email || '';
    const scanCounts = record?.scanCount !== undefined ? record?.scanCount : record?.scanCounts;
    const expiryDate = record?.expiredAt ? dayjs(record?.expiredAt) : (record?.expiryDate ? dayjs(record?.expiryDate) : null);

    editForm.setFieldsValue({
      email: currentEmail,
      scanCounts: scanCounts,
      date: expiryDate,
    });

    setEditModalOpen(true);
  };

  const handleUpdateFacialScan = async (values) => {
    try {
      setEditLoading(true);
      const id = selectedRecord?.rewardId || selectedRecord?.id || selectedRecord?._id;
      const patientId = selectedRecord?.patientId || location.state?.patientId || JSON.parse(localStorage.getItem('editPatientId') || 'null');
      const formattedDate = values.date ? values.date.format('YYYY-MM-DD') : null;
      const payload = {
        patientId,
        email: values.email,
        scanCounts: values.scanCounts,
        scanCount: values.scanCounts,
        facialScans: values.scanCounts,
        expiryDate: formattedDate,
        expiredAt: formattedDate,
      };

      try {
        await axios.post(`${app.URL}/api/v1/app/anura/updatefacial?rewardId=${id}`, payload, { headers: getHeaders() });
      } catch (err) {
        console.log(err)
        // if (err?.response?.status === 404) {
        //   await axios.put(`${app.URL}/api/v1/admin/facialscan/update/${id}`, payload, { headers: getHeaders() });
        // } else {
        //   throw err;
        // }
      }

      message.success('Facial scan updated successfully');
      setEditModalOpen(false);
      editForm.resetFields();
      setSelectedRecord(null);

      // Refresh listing automatically
      handleSearch({
        email: values.email || searchForm.getFieldValue('email'),
      });
    } catch (error) {
      console.log(error);
      message.error(error?.response?.data?.msg || error?.response?.data?.message || 'Something went wrong');
    } finally {
      setEditLoading(false);
    }
  };

  // =========================
  // TABLE COLUMNS
  // =========================
  const columns = [
    {
      title: 'Scan Count',
      dataIndex: 'scanCount',
      key: 'scanCount',
    },
    // {
    //   title: 'Facial Scans',
    //   dataIndex: 'facialScans',
    //   key: 'facialScans',
    // },

    {
  title: 'Expiration Date',
  dataIndex: 'expiredAt',
  key: 'expiredAt',
  render: (text) => (text ? dayjs(text).format('MM/DD/YYYY') : '-'),
  sorter: (a, b) => {
    const dateA = a.expiredAt ? dayjs(a.expiredAt).valueOf() : 0;
    const dateB = b.expiredAt ? dayjs(b.expiredAt).valueOf() : 0;
    return dateA - dateB;
  },
},

{
  title: 'Date Created',
  dataIndex: 'createdAt',
  key: 'createdAt',
  render: (text) => (text ? dayjs(text).format('MM/DD/YYYY') : '-'),
  sorter: (a, b) => {
    const dateA = a.createdAt ? dayjs(a.createdAt).valueOf() : 0;
    const dateB = b.createdAt ? dayjs(b.createdAt).valueOf() : 0;
    return dateA - dateB;
  },
},
    // {
    //   title: 'Expiration Date',
    //   dataIndex: 'expiredAt',
    //   key: 'expiredAt',
    //   render: (text) => (text ? dayjs(text).format('MM/DD/YYYY') : '-'),
    // },
    // {
    //   title: 'Date Created',
    //   dataIndex: 'createdAt',
    //   key: 'createdAt',
    //   render: (text) => (text ? dayjs(text).format('MM/DD/YYYY') : '-'),
    // },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      render: (_, record) => (
        <img
          className='cursor-pointer'
          style={{ width: '22px', cursor: 'pointer' }}
          alt='edit btn img'
          src={editBtnImg}
          onClick={() => handleOpenEditModal(record)}
        />
      ),
    },
  ];

  return (
    <div className='dashboard-card container-fluid  '>
      {/* HEADER */}
      <div className='row p-0 mb-4'>
        <div
          className='for-before col-12 col-lg-6 col-md-8 head fs-3 fw-bold p-3'
          style={{
            borderBottomRightRadius: '10px',
            color: '#FFFFFF',
          }}
        >
          Vital Scan Management
        </div>
      </div>

      {/* SEARCH + ADD BUTTON */}
      <div className='row mb-4 align-items-end'>
        <div className='col-md-8'>
          <Form form={searchForm} layout='vertical' onFinish={handleSearch}>
            <div className='row'>
              <div className='col-md-8'>
                <Form.Item
                  label='Email'
                  name='email'
                  rules={[
                    {
                      required: true,
                      message: 'Please enter email',
                    },
                    {
                      type: 'email',
                      message: 'Please enter valid email',
                    },
                  ]}
                >
                  <Input placeholder='Enter email' size='large' />
                </Form.Item>
              </div>

              <div className='col-md-4 d-flex align-items-end'>
                <Form.Item>
                  <Button type='primary' htmlType='submit' loading={loading} size='large'>
                    Search
                  </Button>
                </Form.Item>
              </div>
            </div>
          </Form>
        </div>

        <div
          className='col-md-4 text-end   '
          style={{
            marginBottom: '20px',
          }}
        >
          <Button
            type='primary'
            size='large'
            onClick={() => {
              const email = searchForm.getFieldValue('email');

              addForm.setFieldsValue({
                email,
              });

              setOpenModal(true);
            }}
          >
            Add Vital Scan
          </Button>
        </div>
      </div>

      {/* LOADER */}
      {loading ? (
        <div className='text-center mt-5'>
          <Spin size='large' />
        </div>
      ) : (
        <>
          {/* TABLE */}
          {showTable && (
            <div className='row mt-4'>
              <div className='col-12'>
                <div className='d-flex justify-content-between align-items-center mb-3'>
                  <div className='fs-5 fw-bold'>Total Reward Count : {rewardCount}</div>

                  {/* <div className='fs-6 fw-semibold'>Remaining : {rewardCount}</div> */}
                </div>

                <Table
                  columns={columns}
                  dataSource={tableData}
                  rowKey='rewardId'
                  bordered
                  pagination={{
                    pageSize: 10,
                    total: tableData.length,
                    showSizeChanger: true,
                  }}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* ADD MODAL */}
      <Modal
        title='Add Vital Scan'
        open={openModal}
        onCancel={() => {
          setOpenModal(false);
          addForm.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={addForm} layout='vertical' onFinish={handleAddFacialScan}>
          {/* EMAIL */}
          <Form.Item
            label='Email'
            name='email'
            rules={[
              {
                required: true,
                message: 'Please enter email',
              },
              {
                type: 'email',
                message: 'Please enter valid email',
              },
            ]}
          >
            <Input placeholder='Enter email' size='large' />
          </Form.Item>

          {/* SCAN COUNT */}
          <Form.Item
            label='Scan Count'
            name='scanCounts'
            rules={[
              {
                required: true,
                message: 'Please enter scan count',
              },
            ]}
          >
            <InputNumber placeholder='Enter scan count' min={1} size='large' style={{ width: '100%' }} />
          </Form.Item>

          {/* DATE */}
          <Form.Item
            label='Expiry Date'
            name='date'
            rules={[
              {
                required: true,
                message: 'Please select expiry date',
              },
            ]}
          >
            <DatePicker
              style={{ width: '100%' }}
              size='large'
              disabledDate={(current) => {
                return current && current < dayjs().startOf('day');
              }}
            />
          </Form.Item>

          {/* SUBMIT */}
          <Form.Item className='mb-0'>
            <div className='d-flex justify-content-end gap-2'>
              <Button
                onClick={() => {
                  setOpenModal(false);
                  addForm.resetFields();
                }}
              >
                Cancel
              </Button>

              <Button type='primary' htmlType='submit' loading={addLoading}>
                Submit
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* EDIT MODAL */}
      <Modal
        title='Edit Vital Scan'
        open={editModalOpen}
        onCancel={() => {
          setEditModalOpen(false);
          editForm.resetFields();
          setSelectedRecord(null);
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={editForm} layout='vertical' onFinish={handleUpdateFacialScan}>
          {/* EMAIL */}
          <Form.Item
            label='Email'
            name='email'
            rules={[
              {
                required: true,
                message: 'Please enter email',
              },
              {
                type: 'email',
                message: 'Please enter valid email',
              },
            ]}
          >
            <Input placeholder='Enter email' size='large' />
          </Form.Item>

          {/* SCAN COUNT */}
          <Form.Item
            label='Scan Count'
            name='scanCounts'
            rules={[
              {
                required: true,
                message: 'Please enter scan count',
              },
            ]}
          >
            <InputNumber placeholder='Enter scan count' min={0} size='large' style={{ width: '100%' }} />
          </Form.Item>

          {/* DATE */}
          <Form.Item
            label='Expiry Date'
            name='date'
            rules={[
              {
                required: true,
                message: 'Please select expiry date',
              },
            ]}
          >
            <DatePicker
              style={{ width: '100%' }}
              size='large'
            />
          </Form.Item>

          {/* SUBMIT */}
          <Form.Item className='mb-0'>
            <div className='d-flex justify-content-end gap-2'>
              <Button
                onClick={() => {
                  setEditModalOpen(false);
                  editForm.resetFields();
                  setSelectedRecord(null);
                }}
              >
                Cancel
              </Button>

              <Button type='primary' htmlType='submit' loading={editLoading}>
                Update
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FacialScanViewAll;